import { makeStyles } from 'tss-react/mui';
import { Switch } from '@mui/material';
import { useDisclosure } from 'further-ui/hooks';
import { useAllocation } from 'hooks/data/allocation/useAllocation';
import { useNotification } from 'hooks/ui/useNotification';
import AlertDialog from 'components/AlertDialog';
import { FC, useState } from 'react';
import { AppDatePicker } from 'components/FormElements';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';

const useStyles = makeStyles()(() => ({
  taxTreamentSwitch: {
    display: 'flex',
    gridGap: '0.5rem',
  },
  freezeDate: {
    marginTop: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    '&>label': {
      flex: 'none',
      marginRight: '25px',
    },
  },
  warningText: {
    width: '90%',
    margin: '0 auto',
    textAlign: 'center',
  },
}));

type Props = {
  value: boolean;
  shareAllocationUploadId: string;
  processCompleted: boolean;
  exitId?: string;
};

const TaxReliefElegibilitySwitch: FC<Props> = ({
  value,
  shareAllocationUploadId,
  processCompleted,
  exitId,
}) => {
  const { classes } = useStyles();
  const { error } = useNotification();
  const taxReliefStatusDisclosure = useDisclosure();
  const { update } = useAllocation({ params: { id: shareAllocationUploadId } });
  const [lostReliefDate, setlostReliefDate] = useState<Date | null>(new Date());
  const [canEditSeis] = useHasPermissions([RolePermission.EditSeis]);

  const isShareConsiderationExit = !!exitId;

  if (update.isPending) return <div>Updating...</div>;

  const handleChangeTaxReliefStatus = async (
    taxReliefElegible: boolean,
    lostReliefDate: Date | null,
  ) => {
    try {
      await update.mutateAsync(
        {
          taxReliefElegible,
          lostReliefDate,
        },
        {
          onSuccess: () => {
            setlostReliefDate(new Date());
          },
        },
      );
    } catch {
      error('Error updating the S/EIS eligibility.');
    }
    taxReliefStatusDisclosure.onClose();
  };

  return (
    <>
      <div className={classes.taxTreamentSwitch}>
        <span>No</span>
        <Switch
          disabled={update.isPending || !canEditSeis}
          checked={value}
          onChange={(e) => {
            if (e.target.checked) {
              handleChangeTaxReliefStatus(true, null);
            } else {
              taxReliefStatusDisclosure.onOpen();
            }
          }}
        />
        <span>Yes</span>
      </div>
      <AlertDialog
        open={taxReliefStatusDisclosure.isOpen}
        onClose={taxReliefStatusDisclosure.onClose}
        onConfirm={() => handleChangeTaxReliefStatus(false, lostReliefDate)}
        title="Warning"
        content={
          <>
            {isShareConsiderationExit ? (
              <p className={classes.warningText}>
                Toggling this allocation's S/EIS status off will mean the
                disposal will be treated as non-S/EIS eligible at exit.
              </p>
            ) : processCompleted ? (
              <p className={classes.warningText}>
                Toggling this allocation's S/EIS status off will list the
                allocation in the <i>Lost tax relief</i> column of the relevant
                annual tax report.
              </p>
            ) : (
              <p className={classes.warningText}>
                Toggling this allocation's S/EIS status off will remove the
                allocation from the relevant annual tax report for the purpose
                of reporting initial income tax relief.
              </p>
            )}
            {processCompleted ? (
              <div className={classes.freezeDate}>
                <label htmlFor="lostReliefDate">Lost relief date:</label>
                <AppDatePicker
                  id="lostReliefDate"
                  name="lostReliefDate"
                  value={lostReliefDate}
                  onChange={(date) => {
                    setlostReliefDate(date);
                  }}
                  disableFuture={true}
                />
              </div>
            ) : null}
          </>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Continue',
        }}
      />
    </>
  );
};

export default TaxReliefElegibilitySwitch;
